import BusinessIcon from "@mui/icons-material/Business";
import { DialogActions, DialogContent } from "@mui/material";
import {
  Autocomplete,
  Button,
  CreatedUpdatedBy,
  DatePicker,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  TextField,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { createContext } from "react";
import usePartnerCommissionForm, {
  UsePartnerCommissionFormProps,
  UsePartnerCommissionFormReturnType,
} from "./usePartnerCommissionForm";

export const PartnerCommissionContext = createContext<
  UsePartnerCommissionFormReturnType | undefined
>(undefined);

export interface PartnerCommissionFormProps extends UsePartnerCommissionFormProps {}

export default function PartnerCommissionForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: PartnerCommissionFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = usePartnerCommissionForm({
    id,
    afterSave,
    defaultValues,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
  } = form;
  const title = isCreate
    ? I18n.t("PartnerCommission.addTitle")
    : `Commission for Partner ${item.partner_id}`;
  const subtitle = isCreate ? undefined : undefined;
  console.log({ item });
  return (
    <ParamsContext.Provider value={{ partner_commission_id: id }}>
      <PartnerCommissionContext.Provider value={form}>
        <PageHeader
          avatar={<BusinessIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="PartnerCommission.attributes"
            translationPlaceholdersCategory="PartnerCommission.placeholders"
            translationHelpersCategory="PartnerCommission.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("partner_commission", item)}
          >
            <FieldsContainer>
              {!params?.partner_id && (
                <Field fullWidth>
                  <TextField id="partner_id" />
                </Field>
              )}
              <Field fullWidth>
                <DatePicker id="valid_from" />
              </Field>
              <Field fullWidth>
                <Autocomplete id="program_id" />
              </Field>
              <Field fullWidth>
                <TextField id="headcount" />
              </Field>
              <Field fullWidth>
                <TextField id="commission_rate" />
              </Field>
            </FieldsContainer>
          </Form>
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="PartnerCommission"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </PartnerCommissionContext.Provider>
    </ParamsContext.Provider>
  );
}
